export interface WaitingMentionLike {
  message: {
    createdAt?: unknown;
  };
}

export const WAITING_MENTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export function recentWaitingMentions<T extends WaitingMentionLike>(
  mentions: readonly T[],
  nowMs = Date.now()
): T[] {
  const cutoff = nowMs - WAITING_MENTION_WINDOW_MS;
  return mentions.filter((hit) => {
    const createdAt = hit.message.createdAt as { toMillis?: () => number } | null | undefined;
    return (createdAt?.toMillis?.() ?? 0) >= cutoff;
  });
}

/**
 * A MENTION WAITS ONLY WHILE IT IS UNREAD.
 *
 * Bobbie, 2026-09-24, on her phone: "The admin board I keep getting
 * notifications. I have clicked done with this and the same message keeps
 * showing up saying I have a new unread message."
 *
 * recentWaitingMentions above keeps every @mention from the last seven days,
 * and nothing a person does clears it: not reading the conversation, not Mark
 * read, not "Done with this". So Done filed the Admin board row to Past in
 * Messages while the bell, the phone's app badge and the Messages menu badge
 * (useStaffMessageWaitingCount) and the staff home card kept counting the
 * reply that named her, for a week. A fully muted Admin board did not quiet
 * it either, although the same mute already quiets the row itself.
 *
 * `unreadSinceByThreadId` is unreadThreadCursorsFrom's answer: for each
 * thread the Messages row still calls unread, the read cursor it counts from.
 * A mention counts only when its thread is in that map and the mention is
 * newer than the cursor, so every act that reads or files the conversation
 * clears it, the mute clears it, and a mention that has not been read yet
 * still counts exactly as before. The seven day window still applies on top.
 */
export function stillUnreadMentions<T extends WaitingMentionLike & { threadId: string }>(
  mentions: readonly T[],
  unreadSinceByThreadId: ReadonlyMap<string, number>
): T[] {
  return mentions.filter((hit) => {
    if (!unreadSinceByThreadId.has(hit.threadId)) return false;
    const since = unreadSinceByThreadId.get(hit.threadId) ?? 0;
    const createdAt = hit.message.createdAt as { toMillis?: () => number } | null | undefined;
    const createdMs = createdAt?.toMillis?.();
    // A mention with no resolved time cannot be placed against the cursor.
    // It is inside an unread thread, so it keeps counting, never the reverse.
    return typeof createdMs !== 'number' || !Number.isFinite(createdMs) || createdMs > since;
  });
}

/**
 * A MUST-READ IS NOT AN UNREAD, AND NOTHING BUT AN ACKNOWLEDGEMENT CLEARS IT.
 *
 * A message posted with "Acknowledgement required" keeps counting on the staff
 * home and the bell until acks[me] is written, and only answering the message
 * itself writes that: the Understood control, or an emoji reaction on a
 * message that is waiting for your ack, which stamps it the same way (Bobbie,
 * DECISIONS.md 2026-08-18: "They click done or acknowledge or click a
 * reaction which should be the same for ALL messages", and the on-screen
 * prompt says both out loud: "Tap an emoji below or Understood/complete").
 * Reading the conversation does not. Filing it to Past does
 * not. "Done with this" on its notification card does not: it
 * moves the conversation's read and filing cursors, which is a different
 * record entirely. That is correct, and it is also why an ack-required Admin
 * board post can look like "an unread that will not go away" - so the surfaces
 * that keep counting it say MUST READ and link to the ack control, never
 * UNREAD (see StaffHomeContent's attention row, which excludes every
 * must-read thread from its unread bucket).
 *
 * The rule lived inline inside useMyUnacked's snapshot handler, where nothing
 * could test it against a real filing. One thing, one place.
 */
export function messageStillNeedsMyAck(
  message: {
    authorUid?: unknown;
    recalled?: unknown;
    acks?: Record<string, unknown> | null;
  },
  meUid: string
): boolean {
  if (!meUid) return false;
  // My own must-read is not waiting on me, a recalled one is not waiting on
  // anyone, and an ack already stamped is the thing that clears it.
  if (message.authorUid === meUid) return false;
  if (message.recalled) return false;
  return !message.acks?.[meUid];
}

export function waitingOnYouCount(
  unackedCount: number,
  recentMentionCount: number,
  staffAnnouncementCount: number
): number {
  return unackedCount + recentMentionCount + staffAnnouncementCount;
}

/**
 * WHAT A "WAITING ON YOU" NUMBER IS ALLOWED TO CLAIM.
 *
 * Bobbie, 2026-09-29 about 11:25pm Central: "it says i'm all caught up but I
 * have not read some of the messages....or marked them as got it". Two
 * different things can stand behind a count, and they are handled apart:
 *
 *   - 'failed': a check itself failed (a listener error, the thread directory
 *     answering 500). The count is only what survived. The home row used to
 *     sit on "Checking..." forever, and the bell, the phone app badge and the
 *     Messages tile quietly showed whatever was left, including no badge at
 *     all. Now every surface marks the number as a floor ("?" or "3+") and
 *     the home row says the check failed. A failure is expected to pass.
 *
 *   - 'capped': a list came back FULL (more than 200 must-reads naming her,
 *     more than 100 posts in one announcement list), so something older than
 *     its edge was not looked at. Nothing failed, and that list only fills up
 *     with posts that need nothing from her: FYI posts, answered must-reads,
 *     board posts. So it is not treated as a failure anywhere. The bell, the
 *     tile, the menu and the badge show the number found, exactly as for a
 *     complete count; only the home row, and only when it found NOTHING,
 *     says the quieter WAITING_CAPPED_COPY instead of "All clear, nice". A
 *     refute round proved the first version wrong by execution: treating a
 *     full list as unknown put a permanent "?" on every busy account's home,
 *     bell, tile and menu, because a full list never empties.
 *
 * A failure outranks loading: once one check has failed the total is short
 * no matter what the others say.
 */
export type WaitingCountState = 'checking' | 'counted' | 'capped' | 'failed';

export function waitingCountState(input: {
  loading: boolean;
  error: string | null | undefined;
  capped: boolean;
}): WaitingCountState {
  if (input.error) return 'failed';
  if (input.loading) return 'checking';
  if (input.capped) return 'capped';
  return 'counted';
}

/** True when a check failed, so the number shown is only what survived. A
 *  full list is deliberately NOT this; see WaitingCountState above. */
export function waitingCheckFailed(input: { error: string | null | undefined }): boolean {
  return Boolean(input.error);
}

/** The home row's line when a check failed. */
export const WAITING_FAILED_COPY = 'Could not check messages. Try again.';
/**
 * The home row's line when it found nothing but a list was full, so it cannot
 * promise nothing older is waiting. Quieter than All clear on purpose. A copy
 * call Bobbie can change.
 */
export const WAITING_CAPPED_COPY = 'Nothing waiting in your newest messages.';

/**
 * The poster number on the home row, and the pill on the bell and the tiles.
 * A counted number prints as itself. A FAILED one never prints a bare 0:
 * "?" when nothing was found, "3+" when three were. `max` is the pill's own
 * one-digit cap ("9+"), which already reads as "at least".
 */
export function waitingCountLabel(count: number, failed: boolean, max?: number): string {
  const n = Math.max(0, Math.floor(Number.isFinite(count) ? count : 0));
  if (max !== undefined && n > max) return `${max}+`;
  if (!failed) return String(n);
  return n === 0 ? '?' : `${n}+`;
}

/**
 * The Waiting on you row on the staff home, as text: the number it prints,
 * the line under the label when nothing was found, and the extra line it
 * adds under a nonzero breakdown. One place, so it can be tested.
 */
export function waitingRowText(count: number, state: WaitingCountState): {
  number: string;
  zeroLine: string;
  extraLine: string | null;
} {
  const failed = state === 'failed';
  return {
    number: waitingCountLabel(count, failed),
    zeroLine: failed
      ? WAITING_FAILED_COPY
      : state === 'capped'
      ? WAITING_CAPPED_COPY
      : state === 'counted'
      ? 'All clear, nice'
      : 'Checking...',
    // A number above zero speaks for itself when a list was full; only a
    // failed check needs saying under it.
    extraLine: failed && count > 0 ? WAITING_FAILED_COPY : null,
  };
}

/**
 * The staff bell's spoken label. A failed check used to leave the bell on
 * whatever part of the count survived, or on no dot at all, with a label
 * still reading "0 waiting on you". A full list is not a failure and reads
 * as the number found.
 */
export function staffBellLabel(count: number, state: { error: string | null | undefined }): string {
  if (!waitingCheckFailed(state)) return `${count} waiting on you`;
  return count > 0
    ? `At least ${count} waiting on you. Some messages could not be checked.`
    : 'Could not check what is waiting on you';
}

/**
 * WHICH CHECK FAILED, AS A FIXED CODE.
 *
 * Bobbie, 2026-10-26: "it happened again but went away after about 5 mins".
 * The Waiting on you row is fed by several checks (alerts, conversations,
 * read state, must-reads, class posts, announcements, staff posts) and says
 * the same "Could not check messages" whichever one failed, and none of them
 * reached Sentry, so nobody could tell which. messageWaiting.ts now sends one
 * Sentry warning per failing check, naming the check and this code.
 *
 * PRIVACY: only the code goes out, never the error's own words. Every
 * message these checks can carry is matched here; anything else is 'other'.
 */
export type WaitingFailureCode =
  | 'permission-denied'
  | 'missing-index'
  | 'quota'
  | 'rate-limited'
  | 'offline'
  | 'local-database'
  | 'family-access-changed'
  | 'family-repair-needed'
  | 'not-configured'
  | 'listener-retries-spent'
  | 'server-error'
  | 'other';

export function waitingFailureCode(message: string | null | undefined): WaitingFailureCode {
  const text = String(message ?? '').toLowerCase();
  if (text.includes('insufficient permissions') || text.includes('permission-denied')) return 'permission-denied';
  if (text.includes('requires an index') || text.includes('index is currently building')) return 'missing-index';
  if (text.includes('quota') || text.includes('resource-exhausted') || text.includes('resource exhausted')) return 'quota';
  if (
    text.includes('refreshed a lot') ||
    text.includes('updating often') ||
    text.includes('too many requests') ||
    text.includes('too_many_requests')
  ) {
    return 'rate-limited';
  }
  if (
    text.includes('indexeddb') ||
    text.includes('idbdatabase') ||
    text.includes('database connection is closing') ||
    text.includes('in-progress transaction') ||
    text.includes('internal assertion failed')
  ) {
    return 'local-database';
  }
  if (
    text.includes('offline') ||
    text.includes('load failed') ||
    text.includes('failed to fetch') ||
    text.includes('network') ||
    text.includes('unavailable')
  ) {
    return 'offline';
  }
  if (text.includes('family-message access changed')) return 'family-access-changed';
  if (text.includes('need studio repair')) return 'family-repair-needed';
  if (text.includes('not ready on this site')) return 'not-configured';
  if (text.includes('conversations could not load')) return 'listener-retries-spent';
  if (text.includes('could not be checked')) return 'server-error';
  return 'other';
}

/**
 * ONE OUTAGE, ONE REPORT, ONE RECOVERY NOTE, however many copies of the row's
 * hook are mounted (the home card, the bell, the badge and the tiles each run
 * one, and they come and go as she moves around the app). Kept per check for
 * the whole page: an outage starts when the first copy fails and ends only
 * when no copy is failing any more, whether the failing copies came back or
 * were unmounted (Try again remounts the row, and the new copy never saw the
 * failure). So a recovery is always sent, it is timed from the first failure
 * to the last copy clearing, and the same check failing again later in the
 * day is a new outage. A new reason while still down is reported too. Capped
 * per page load so a flapping check can never flood Sentry.
 */
export const WAITING_REPORT_CAP = 20;
export type WaitingReport =
  | { kind: 'failed'; source: string; code: WaitingFailureCode }
  | {
      kind: 'recovered';
      source: string;
      code: WaitingFailureCode;
      seconds: number;
      how: 'recovered' | 'unmounted';
    };

export function createWaitingFailureTracker(cap = WAITING_REPORT_CAP) {
  const outages = new Map<string, {
    at: number;
    code: WaitingFailureCode;
    reported: Set<WaitingFailureCode>;
    copies: Map<string, WaitingFailureCode>;
  }>();
  let sent = 0;
  const allow = () => {
    if (sent >= cap) return false;
    sent += 1;
    return true;
  };
  const clear = (
    source: string,
    copy: string,
    how: 'recovered' | 'unmounted',
    nowMs: number
  ): WaitingReport | null => {
    const outage = outages.get(source);
    if (!outage || !outage.copies.delete(copy) || outage.copies.size > 0) return null;
    outages.delete(source);
    const seconds = Math.max(0, Math.round((nowMs - outage.at) / 1000));
    return allow() ? { kind: 'recovered', source, code: outage.code, seconds, how } : null;
  };
  return {
    failed(source: string, copy: string, code: WaitingFailureCode, nowMs = Date.now()): WaitingReport | null {
      let outage = outages.get(source);
      if (!outage) {
        outage = { at: nowMs, code, reported: new Set(), copies: new Map() };
        outages.set(source, outage);
      }
      outage.copies.set(copy, code);
      outage.code = code;
      if (outage.reported.has(code)) return null;
      outage.reported.add(code);
      return allow() ? { kind: 'failed', source, code } : null;
    },
    recovered(source: string, copy: string, nowMs = Date.now()): WaitingReport | null {
      return clear(source, copy, 'recovered', nowMs);
    },
    unmounted(source: string, copy: string, nowMs = Date.now()): WaitingReport | null {
      return clear(source, copy, 'unmounted', nowMs);
    },
  };
}
